const DEC_DIGITS = 4; /* decimal digits per NBASE digit */

/*
 * Interpretation of high bits.
 */
const NUMERIC_SIGN_MASK = 0xc000;
const NUMERIC_NEG = 0x4000;
const NUMERIC_SPECIAL = 0xc000;

const NUMERIC_FLAGBITS = (n_header: number) => n_header & NUMERIC_SIGN_MASK;
const NUMERIC_IS_SPECIAL = (n: number) =>
  NUMERIC_FLAGBITS(n) === NUMERIC_SPECIAL;

/*
 * Definitions for special values (NaN, positive infinity, negative infinity).
 *
 * The two bits after the NUMERIC_SPECIAL bits are 00 for NaN, 01 for positive
 * infinity, 11 for negative infinity.  (This makes the sign bit match where
 * it is in a short-format value, though we make no use of that at present.)
 * We could mask off the remaining bits before testing the active bits, but
 * currently those bits must be zeroes, so masking would just add cycles.
 */
const NUMERIC_PINF = 0xd000;
const NUMERIC_NINF = 0xf000;

const NUMERIC_IS_PINF = (n: number) => n === NUMERIC_PINF;
const NUMERIC_IS_NINF = (n: number) => n === NUMERIC_NINF;

function trim(str: string) {
  if (!str.includes('.')) return str;

  let end = str.length - 1;

  while (str[end] === '0') --end;

  /* We want to get rid of the decimal point too, if it's now last. */
  if (str[end] === '.') --end;

  return end < str.length ? str.substring(0, end + 1) : str;
}

/*
 * get_str_from_var() -
 *
 *	Convert a var to text representation (guts of numeric_out).
 *	The var is displayed to the number of digits indicated by its dscale.
 *	Returns a palloc'd string.
 */
export const numericParser = (numeric: DataView) => {
  let /* char * */ cp: string;
  let /* int */ i: number;
  let /* int */ d: number;
  let /* NumericDigit */ dig;

  let /* NumericDigit */ d1;

  // Sign: 0x0000 = positive, 0x4000 = negative, 0xC000 = NaN
  const sign = numeric.getUint16(4);

  /*
   * Handle NaN and infinities
   */
  if (NUMERIC_IS_SPECIAL(sign)) {
    if (NUMERIC_IS_PINF(sign)) return 'Infinity';
    else if (NUMERIC_IS_NINF(sign)) return '-Infinity';
    else return 'NaN';
  }

  const ndigits = numeric.getUint16(0);

  // Read all of the groups
  const digits = [];
  for (let i = 0; i < ndigits; i++) {
    digits.push(numeric.getUint16(8 + 2 * i));
  }

  const weight = numeric.getInt16(2); // 10000^weight

  /*
   * i is set to the # of decimal digits before decimal point. dscale is the
   * # of decimal digits we will print after decimal point. We may generate
   * as many as DEC_DIGITS-1 excess digits at the end, and in addition we
   * need room for sign, decimal point, null terminator.
   */
  i = (weight + 1) * DEC_DIGITS;
  if (i <= 0) i = 1;

  cp = '';

  /*
   * Output a dash for negative values
   */
  if (sign === NUMERIC_NEG) {
    cp += '-';
  }

  /*
   * Output all digits before the decimal point
   */
  if (weight < 0) {
    d = weight + 1;
    cp += '0';
  } else {
    for (d = 0; d <= weight; d++) {
      dig = d < ndigits ? digits[d] : 0;
      /* In the first digit, suppress extra leading decimal zeroes */
      let putit: boolean = d > 0;

      d1 = Math.floor(dig / 1000);
      dig -= d1 * 1000;
      putit ||= d1 > 0;
      if (putit) cp += d1;
      d1 = Math.floor(dig / 100);
      dig -= d1 * 100;
      putit ||= d1 > 0;
      if (putit) cp += d1;
      d1 = Math.floor(dig / 10);
      dig -= d1 * 10;
      putit ||= d1 > 0;
      if (putit) cp += d1;
      cp += Math.floor(dig);
    }
  }

  // Number of digits (in base 10) to print after decimal separator
  const dscale = numeric.getInt16(6);

  /*
   * If requested, output a decimal point and all the digits that follow it.
   * We initially put out a multiple of DEC_DIGITS digits, then truncate if
   * needed.
   */
  if (dscale > 0) {
    cp += '.';
    for (i = 0; i < dscale; d++, i += DEC_DIGITS) {
      dig = d >= 0 && d < ndigits ? digits[d] : 0;
      d1 = Math.floor(dig / 1000);
      dig -= d1 * 1000;
      cp += d1;
      d1 = Math.floor(dig / 100);
      dig -= d1 * 100;
      cp += d1;
      d1 = Math.floor(dig / 10);
      dig -= d1 * 10;
      cp += d1;
      cp += Math.floor(dig);
    }
  }

  return trim(cp);
};
